// Forward-geocode an address/POI for the dashboard location-pin search box, via
// the MapBox Search Box API `/forward` (best POI/landmark coverage). Returns a
// *list* of candidates so the owner can pick the right one - POI data can be
// ambiguous (multiple records for the same venue with different coordinates),
// and the search only needs to get them close before they drag the pin exactly.
import { env } from '@/env';

const TOKEN = env.mapboxToken;

export interface GeocodeHit {
  latitude: number;
  longitude: number;
  label: string;
}

export interface SearchOpts {
  /** Bias results near this [lng, lat] (e.g. the current map center). */
  proximity?: [number, number];
  /** ISO language for matching/labels (e.g. 'vi', 'en', 'de', 'fr', 'pl', 'sv', 'nl'). */
  language?: string;
  /** Restrict to a country (ISO-3166 alpha-2, e.g. 'de', 'ch', 'pl', 'se', 'nl', 'vn'). */
  country?: string;
}

const SEARCHBOX_FORWARD = 'https://api.mapbox.com/search/searchbox/v1/forward';
const GEOCODE_V5 = 'https://api.mapbox.com/geocoding/v5/mapbox.places';
const LIMIT = 6;

interface Feature {
  geometry?: { coordinates?: [number, number] };
  center?: [number, number];
  properties?: { name?: string; full_address?: string; place_formatted?: string };
  place_name?: string;
}

function toHit(f: Feature): GeocodeHit | null {
  const c = f.geometry?.coordinates ?? f.center;
  if (!c || c.length < 2) return null;
  const [longitude, latitude] = c;
  const label =
    f.properties?.full_address ??
    (f.properties?.name
      ? `${f.properties.name}${f.properties.place_formatted ? ` · ${f.properties.place_formatted}` : ''}`
      : undefined) ??
    f.place_name ??
    `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
  return { latitude, longitude, label };
}

/** Search for address/POI candidates. Empty array when unconfigured / no match. */
export async function searchPlaces(query: string, opts: SearchOpts = {}): Promise<GeocodeHit[]> {
  if (!TOKEN || !query.trim()) return [];
  const token = encodeURIComponent(TOKEN);
  const q = encodeURIComponent(query);
  const extra =
    (opts.proximity ? `&proximity=${opts.proximity[0]},${opts.proximity[1]}` : '') +
    (opts.language ? `&language=${encodeURIComponent(opts.language)}` : '') +
    (opts.country ? `&country=${encodeURIComponent(opts.country.toLowerCase())}` : '');

  try {
    const sb = await fetch(
      `${SEARCHBOX_FORWARD}?q=${q}&limit=${LIMIT}${extra}&access_token=${token}`,
    );
    if (sb.ok) {
      const data = (await sb.json()) as { features?: Feature[] };
      const hits = (data.features ?? []).map(toHit).filter((h): h is GeocodeHit => h !== null);
      if (hits.length) return hits;
    }
    const v5 = await fetch(`${GEOCODE_V5}/${q}.json?limit=${LIMIT}${extra}&access_token=${token}`);
    if (v5.ok) {
      const data = (await v5.json()) as { features?: Feature[] };
      return (data.features ?? []).map(toHit).filter((h): h is GeocodeHit => h !== null);
    }
    return [];
  } catch {
    return [];
  }
}
